import { useCallback, useContext, useMemo } from 'react'

import { EDITOR_CUBOID_QUICK_SIZE, EDITOR_REQUIRED_ANCHORS, EDITOR_TOOLS } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import { PointArray } from '@editor/interfaces/attribute'
import { CanvasConfig, CanvasEventsExtra } from '@editor/interfaces/editor'
import { fixVertexOnNormal, pointArrayToVector3, pointsToVector3s } from '@editor/services/Points'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Vector3 } from 'three'

import { setMouseAnchor, setWorkingMaskCuboid } from '../store'

/**
 * Main canvas hook for PCD Trim tool.
 * Contains the logic for adding a cuboid (and the points required for it).
 *
 * The behavior is as follows:
 * - If the user clicks on the canvas, a new point is added to the cuboid.
 * - Or if the user triple clicks on the canvas, a new cuboid is created with the clicked point as the center.
 * - If the user has added 4 points, the cuboid is created.
 *
 * @param props
 */
export default function useMainCanvas(): CanvasConfig {
  // Context
  const { selectedTool, changeIsToolProcessing } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const cuboidAnchor = useSelector((state: RootState) => state.maskPCD.workingMaskCuboid)
  const mouseAnchor = useSelector((state: RootState) => state.maskPCD.mouseAnchor)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === EDITOR_TOOLS.PCD_TRIM_CUBOID, [selectedTool])

  /**
   * Add a new point for a cuboid.
   *
   * @param addedPoint New point to be added
   */
  const addCuboidAnchor = (addedPoint: Vector3) => {
    if (cuboidAnchor && cuboidAnchor.points.length >= EDITOR_REQUIRED_ANCHORS.cuboid) {
      return
    }

    const points =
      cuboidAnchor?.points.length === EDITOR_REQUIRED_ANCHORS.cuboid - 1
        ? fixVertexOnNormal(pointsToVector3s(cuboidAnchor.points))
        : pointsToVector3s(cuboidAnchor?.points || [])
    const newPoints = [...points, addedPoint]

    // Keep drawing live line if required points are not enough
    changeIsToolProcessing(newPoints.length < EDITOR_REQUIRED_ANCHORS.cuboid)
    dispatch(setMouseAnchor(undefined))
    dispatch(
      setWorkingMaskCuboid({
        diameter: cuboidAnchor?.diameter || 0,
        points: newPoints.map((point) => point.toArray() as PointArray),
      }),
    )
  }

  /**
   * On mouse move, set the processing anchor to the current mouse position.
   */
  const onMove = useCallback(
    (pcdPoint: PointArray | undefined, solidMeshPoint: PointArray | undefined) => {
      const point = solidMeshPoint || pcdPoint
      if (
        isToolSelected &&
        point &&
        (!cuboidAnchor || (cuboidAnchor && cuboidAnchor?.points.length < EDITOR_REQUIRED_ANCHORS.cuboid))
      ) {
        dispatch(setMouseAnchor(point))
      }
    },
    [isToolSelected, cuboidAnchor, dispatch],
  )

  return {
    objects: {
      circleAnchors: useMemo(() => {
        const anchors = []
        if (cuboidAnchor?.points.length && cuboidAnchor.points.length < EDITOR_REQUIRED_ANCHORS.cuboid) {
          anchors.push(
            ...cuboidAnchor.points.map((point) => ({
              id: `cuboid-anchor-${point.join('-')}`,
              point,
              color: '#777',
            })),
          )
        }

        if (mouseAnchor) {
          anchors.push({ id: `maskregion-mouse-anchor`, point: mouseAnchor, color: '#777' })
        }

        return anchors
      }, [cuboidAnchor?.points, mouseAnchor]),
    },
    events: {
      onMouseUp: (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        point: PointArray | undefined,
        solidMeshPoint: PointArray | undefined,
        { isTripleClicked, isDragged }: CanvasEventsExtra,
      ) => {
        const selectedPoint = solidMeshPoint || point
        if (!isToolSelected || !selectedPoint || isDragged) return

        if (!isTripleClicked || (cuboidAnchor?.points.length && cuboidAnchor.points.length >= 3)) {
          // prevent miss clicking on the same point
          if (
            !cuboidAnchor?.points.length ||
            !pointArrayToVector3(selectedPoint).equals(
              pointArrayToVector3(cuboidAnchor.points[cuboidAnchor.points.length - 1]),
            )
          ) {
            addCuboidAnchor(pointArrayToVector3(selectedPoint))
          }
        } else {
          // Quick create a cuboid with triple click
          const size = EDITOR_CUBOID_QUICK_SIZE
          dispatch(
            setWorkingMaskCuboid({
              diameter: 0,
              points: [
                [selectedPoint[0] - size, selectedPoint[1] - size, selectedPoint[2] - size],
                [selectedPoint[0] + size, selectedPoint[1] - size, selectedPoint[2] - size],
                [selectedPoint[0] + size, selectedPoint[1] - size, selectedPoint[2] + size],
                [selectedPoint[0] + size, selectedPoint[1] + size, selectedPoint[2] + size],
              ],
            }),
          )
          changeIsToolProcessing(false)
        }
      },
      onMove,
    },
  }
}
